import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext
} from "react";
import { AppStateContext } from "../../containers/App";
import debug from "../../utils/debug";
import {
  hasAndIsNotEmpty,
  calcPropertiesTotalLength
} from "../../utils/helpers";
import api, { VerifySalesPersonIdResponse } from "../../utils/api";
import useT, { TFunction } from "../../hooks/useT";
import Button from "../Button";
import Errors from "../Errors";
import Field from "../Field";
import "./VerifySalesPersonForm.css";

export interface VerifySalesPersonFormProps {
  /** Event handler for when the operation is cancelled. */
  onCancel?: React.MouseEventHandler;
  /** Event handler for when any local errors occur. */
  onError?: (
    state: VerifySalesPersonFormState,
    errors: VerifySalesPersonFormErrors
  ) => void;
  /** Function for checking if the form should continue submitting. */
  shouldSubmit?: (
    state: VerifySalesPersonFormState,
    errors: VerifySalesPersonFormErrors
  ) => void | boolean;
  /** Event handler for when the sales person has been verified. */
  onVerifySuccess?: (response: VerifySalesPersonIdResponse) => void;
  /** Event handler for when any remote error occurred. */
  onVerifyError?: (response: VerifySalesPersonIdResponse) => void;
}

export interface VerifySalesPersonFormState {
  /** The ID of the sales person attempting to verify. */
  salesPersonId: string;
}

export interface VerifySalesPersonFormErrors {
  /** The various errors for each formState property. */
  [fieldKey: string]: React.ReactNodeArray;
  /** The response errors received from the API. */
  response: React.ReactNodeArray;
}

/**
 * Validate the form state and output any error messages.
 */
function validateFormState(
  formState: VerifySalesPersonFormState,
  t: TFunction
): VerifySalesPersonFormErrors {
  const updateFormErrors: VerifySalesPersonFormErrors = {
    salesPersonId: [],
    response: []
  };

  if (!hasAndIsNotEmpty(formState, "salesPersonId")) {
    updateFormErrors.salesPersonId.push(
      t("verifySalesPerson.fields.identifier.errors.required")
    );
  }

  return updateFormErrors;
}

export default function VerifySalesPersonForm({
  onCancel,
  onError,
  shouldSubmit,
  onVerifySuccess,
  onVerifyError
}: VerifySalesPersonFormProps) {
  const { t, superT } = useT();
  const [appState] = useContext(AppStateContext);
  const config = appState.config;

  // Internal state
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<VerifySalesPersonFormState>({
    salesPersonId: ""
  });
  const [formErrors, setFormErrors] = useState<VerifySalesPersonFormErrors>({
    salesPersonId: [],
    response: []
  });

  const handleFormSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();

      // Check for errors and halt if any are present
      debug("[VerifySalesPersonForm] check for any errors");
      const hasErrors = validateFormState(formState, t);
      const numErrors = calcPropertiesTotalLength(hasErrors);
      if (numErrors > 0) {
        setFormErrors(hasErrors);
        if (!!onError) {
          debug("[VerifySalesPersonForm] found errors", { hasErrors });
          onError(formState, hasErrors);
        }

        return;
      }

      // Only continue if it should be allowed to
      if (shouldSubmit && shouldSubmit(formState, hasErrors) === false) {
        debug("[VerifySalesPersonForm] shouldSubmit prevented continuing...");
        return;
      }

      debug("[VerifySalesPersonForm] setLoading: true");
      setLoading(true);

      api
        .verifySalesPersonId(formState.salesPersonId)
        .then(res => {
          const updateFormErrors: VerifySalesPersonFormErrors = {
            ...formErrors,
            salesPersonId: [],
            response: []
          };

          debug("[VerifySalesPersonForm] setLoading: false");
          setLoading(false);

          if (res.success && !!onVerifySuccess) {
            debug("[VerifySalesPersonForm] fire onVerifySuccess");
            onVerifySuccess(res);
            return;
          }

          if (!res.success) {
            // Server error
            if (
              res.error &&
              res.error !== "USER_NOT_FOUND" &&
              res.error.toLowerCase().indexOf("not found") < 0
            ) {
              updateFormErrors.response.push(
                superT("verifySalesPerson.errors.connection")
              );
            }
            // Otherwise output that the ID is invalid
            else {
              updateFormErrors.salesPersonId.push(
                superT("verifySalesPerson.fields.identifier.errors.invalid")
              );
            }

            if (!!onVerifyError) {
              onVerifyError(res);
            }
          }

          // Output the response errors
          if (calcPropertiesTotalLength(updateFormErrors) > 0) {
            if (inputRef.current) {
              inputRef.current.focus();
            }

            setFormErrors(updateFormErrors);
          }
        })
        .catch(error => {
          debug("[VerifySalesPersonForm] has error", { error });

          debug("[VerifySalesPersonForm] setLoading: false");
          setLoading(false);

          // Populate the response with the returned errors
          setFormErrors({
            ...formErrors,
            salesPersonId: [],
            response: [`${error}`]
          });

          if (!!onVerifyError) {
            onVerifyError(error);
          }
        });
    },
    [t, formState, formErrors]
  );

  const handleFieldChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateFormState = {
        ...formState,
        [event.target.name]: config.customise.verifySalesPerson.capitaliseId
          ? event.target.value.toUpperCase()
          : event.target.value
      };
      setFormState(updateFormState);
    },
    [formState]
  );

  // Focus the input field when mounted
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef.current]);

  return (
    <form className="verify-salesperson-form" onSubmit={handleFormSubmit}>
      <header className="verify-salesperson-header">
        <h3 className="verify-salesperson-title">
          {superT("verifySalesPerson.page.title")}
        </h3>
        <p className="verify-salesperson-intro">
          {superT("verifySalesPerson.page.intro")}
        </p>
        <Errors classNamePrefix="form" errors={formErrors.response} />
      </header>
      <Field
        id="verify-salesperson-id"
        type="password"
        name="salesPersonId"
        label={t("verifySalesPerson.fields.identifier.label")}
        icon="id"
        value={formState.salesPersonId}
        errors={formErrors.salesPersonId}
        onChange={handleFieldChange}
        required
        inputProps={{
          ref: inputRef,
          autoComplete: "current-password"
        }}
      />
      <div className="button-group">
        <Button
          type="submit"
          theme="primary"
          shape="solid"
          icon={loading ? "loading" : undefined}
          label={t("verifySalesPerson.buttons.submit.label")}
          fullWidth
          disabled={loading}
        />
        {!!onCancel && (
          <Button
            label={t("verifySalesPerson.buttons.cancel.label")}
            onClick={onCancel}
            className="button-role-cancel"
            fullWidth
          />
        )}
      </div>
    </form>
  );
}
